import React, { useEffect, useState } from "react";
import API_BASE from "../config/api";
import { adminFetch } from "../utils/adminAuth";

/*
  CR #2 — "Admin sees roll-up totals but balances remain separate."
  Everything on this screen is a read-only aggregate over the wallet
  ledger (wallet_engine.php) — nothing here can change a balance.
*/
export default function AdminWalletOverview() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        adminFetch(`${API_BASE}/api/get_wallet_overview.php`)
            .then((r) => r.json())
            .then((res) => { if (res.success) setData(res); })
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="p-3">Loading wallet overview...</div>;
    if (!data) return <div className="alert alert-danger m-3">Could not load wallet overview.</div>;

    const { totals, topCustomers, topAgents, recent } = data;
    const fmt = (n) => `₹ ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    return (
        <div>
            <h5 className="fw-bold mb-1">Wallet Overview</h5>
            <p className="text-secondary small mb-3">
                Roll-up totals across every login's wallet. These are aggregates only — each login's balance stays separate and
                is never merged.
            </p>

            <div className="row g-3 mb-4">
                <div className="col-md-3">
                    <div className="card shadow-sm h-100"><div className="card-body">
                        <div className="text-secondary small">Total Customer Balance</div>
                        <div className="h4 fw-bold mb-0">{fmt(totals.customer_total)}</div>
                        <div className="text-secondary small">{totals.customer_count} wallet(s)</div>
                    </div></div>
                </div>
                <div className="col-md-3">
                    <div className="card shadow-sm h-100"><div className="card-body">
                        <div className="text-secondary small">Total Agent Balance</div>
                        <div className="h4 fw-bold mb-0">{fmt(totals.agent_total)}</div>
                        <div className="text-secondary small">{totals.agent_count} wallet(s)</div>
                    </div></div>
                </div>
                <div className="col-md-3">
                    <div className="card shadow-sm h-100"><div className="card-body">
                        <div className="text-secondary small">Admin Balance (roll-up)</div>
                        <div className="h4 fw-bold mb-0">{fmt(totals.customer_total + totals.agent_total)}</div>
                        <div className="text-secondary small">sum of all wallets — for visibility only</div>
                    </div></div>
                </div>
            </div>

            <div className="row g-3">
                <div className="col-md-6">
                    <div className="card shadow-sm">
                        <div className="card-header bg-light fw-semibold">Top Customer Wallets</div>
                        <div className="card-body p-0">
                            <table className="table table-sm mb-0">
                                <thead className="table-light"><tr><th>Customer</th><th className="text-end">Balance</th></tr></thead>
                                <tbody>
                                    {topCustomers.length === 0 ? (
                                        <tr><td colSpan="2" className="text-secondary p-3">No customer wallets yet.</td></tr>
                                    ) : topCustomers.map((c) => (
                                        <tr key={c.owner_uid}>
                                            <td className="small">{c.fullname || c.owner_uid}<br /><span className="text-secondary">{c.email}</span></td>
                                            <td className="text-end fw-semibold">{fmt(c.balance)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="card shadow-sm">
                        <div className="card-header bg-light fw-semibold">Top Agent Wallets</div>
                        <div className="card-body p-0">
                            <table className="table table-sm mb-0">
                                <thead className="table-light"><tr><th>Agent UID</th><th className="text-end">Balance</th></tr></thead>
                                <tbody>
                                    {topAgents.length === 0 ? (
                                        <tr><td colSpan="2" className="text-secondary p-3">No agent wallets yet.</td></tr>
                                    ) : topAgents.map((a) => (
                                        <tr key={a.owner_uid}>
                                            <td className="small">{a.owner_uid}</td>
                                            <td className="text-end fw-semibold">{fmt(a.balance)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card shadow-sm mt-3">
                <div className="card-header bg-light fw-semibold">Recent Wallet Activity (all logins)</div>
                <div className="card-body p-0">
                    <table className="table table-sm mb-0">
                        <thead className="table-light">
                            <tr><th>Who</th><th>Type</th><th>Direction</th><th className="text-end">Amount</th><th className="text-end">Balance After</th><th>Description</th><th>When</th></tr>
                        </thead>
                        <tbody>
                            {recent.length === 0 ? (
                                <tr><td colSpan="7" className="text-secondary p-3">No wallet activity yet.</td></tr>
                            ) : recent.map((r, i) => (
                                <tr key={i}>
                                    <td className="small">{r.owner_type} · {r.owner_uid}</td>
                                    <td><span className="badge bg-secondary">{r.txn_type}</span></td>
                                    <td>
                                        <span className={`badge bg-${r.direction === "credit" ? "success" : "danger"}`}>{r.direction}</span>
                                    </td>
                                    <td className="text-end">{fmt(r.amount)}</td>
                                    <td className="text-end">{fmt(r.balance_after)}</td>
                                    <td className="small">{r.description}</td>
                                    <td className="text-secondary small">{r.created_at}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
